import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Checkbox from '@zenbusiness/zui/Checkbox';
import FormControl from '@zenbusiness/zui/FormControl';

import * as Styled from './MultiSelectField.style';

/**
 * Multi select field using checkboxes with card-style options
 * @param {object} props - Component props
 * @param {object} props.def - Field definition
 * @param {Array} props.value - Array of selected values
 * @param {Function} props.onChange - Change handler
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @returns {import('react').JSX.Element} - Multi select field component
 */
function MultiSelectField({ def, value = [], onChange, disabled = false, error }) {
  const handleOptionClick = (optionId, optionLabel, isCurrentlyChecked) => {
    const currentValues = Array.isArray(value) ? value : [];

    let newValues;
    if (!isCurrentlyChecked) {
      // Add to selection if within limits
      if (!def.maxSelected || currentValues.length < def.maxSelected) {
        newValues = [...currentValues, { selectedId: optionId, value: optionLabel }];
      } else {
        return; // Don't add if at limit
      }
    } else {
      // Remove from selection
      newValues = currentValues.filter((opt) => opt.selectedId !== optionId);
    }

    onChange(newValues);
  };

  const fieldId = `multi-select-${def.id}`;
  const hasError = Boolean(error);
  const currentValues = Array.isArray(value) ? value : [];
  const isAtLimit = def.maxSelected && currentValues.length >= def.maxSelected;

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      error={hasError}
      fullWidth
      required={def.required}
    >
      {def.label && (
        <Styled.FieldLabel component="legend" id={`${fieldId}-label`} required={def.required}>
          {def.label}
          {def.maxSelected && (
            <span style={{ fontSize: '0.875rem', fontWeight: 'normal', marginLeft: '8px' }}>
              (Select up to {def.maxSelected})
            </span>
          )}
        </Styled.FieldLabel>
      )}

      {def.description && <Styled.FieldDescription>{def.description}</Styled.FieldDescription>}

      <Box mt={2}>
        <Styled.OptionsContainer
          aria-labelledby={`${fieldId}-label`}
          columns={def.columns || 1}
          role="group"
        >
          {def.options?.map((option) => {
            const isChecked = currentValues.some((opt) => opt.selectedId === option.id);
            const isOptionDisabled = disabled || (isAtLimit && !isChecked);

            return (
              <Styled.OptionCard
                key={option.id}
                isDisabled={isOptionDisabled}
                isSelected={isChecked}
                onClick={() =>
                  !isOptionDisabled && handleOptionClick(option.id, option.label, isChecked)
                }
              >
                <Styled.CheckboxWrapper>
                  <Checkbox
                    checked={isChecked}
                    color="primary"
                    disabled={isOptionDisabled}
                    inputProps={{
                      'aria-label': option.label,
                      name: `${fieldId}-${option.id}`
                    }}
                    onChange={() => handleOptionClick(option.id, option.label, isChecked)}
                  />
                </Styled.CheckboxWrapper>

                <Styled.OptionContent>
                  {option.superscript && (
                    <Styled.OptionSuperscript>{option.superscript}</Styled.OptionSuperscript>
                  )}
                  <Styled.OptionLabel>{option.label}</Styled.OptionLabel>
                  {option.subtitle && (
                    <Styled.OptionSubtitle>{option.subtitle}</Styled.OptionSubtitle>
                  )}
                </Styled.OptionContent>
              </Styled.OptionCard>
            );
          })}
        </Styled.OptionsContainer>
      </Box>

      {(error || def.helpText) && (
        <Styled.FieldHelperText>{error || def.helpText}</Styled.FieldHelperText>
      )}
    </FormControl>
  );
}

MultiSelectField.propTypes = {
  def: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['multi_select']).isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    maxSelected: PropTypes.number,
    columns: PropTypes.oneOf([1, 2]),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        superscript: PropTypes.string,
        subtitle: PropTypes.string
      })
    ).isRequired
  }).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string
};

export default MultiSelectField;
